import { Link } from "react-router-dom";
import type { Article } from "@/mocks/news";

type HeroSectionProps = {
  featured: Article;
};

const stats = [
  { value: "4", label: "内容频道" },
  { value: "每日", label: "热点更新" },
  { value: "72h", label: "事件时间线" },
  { value: "理性", label: "吃瓜主张" },
];

export default function HeroSection({ featured }: HeroSectionProps) {
  return (
    <section className="relative overflow-hidden bg-primary-950" aria-label="网站首屏介绍">
      <div className="absolute inset-0">
        <img
          src="https://readdy.ai/api/search-image?query=Stylized%20cinematic%20illustration%20of%20an%20entertainment%20industry%20night%20scene%20with%20stage%20spotlights%2C%20camera%20flashes%20and%20hazy%20smoke%20behind%20a%20velvet%20curtain%2C%20deep%20crimson%20and%20warm%20amber%20tones%2C%20dramatic%20moody%20atmosphere%2C%20editorial%20magazine%20visual%20with%20film%20grain&width=1600&height=1000&seq=mrcg-hero-01&orientation=landscape"
          alt="娱乐圈夜晚舞台与闪光灯意象配图"
          title="美日吃瓜 - 娱乐圈每日热点速递"
          className="h-full w-full object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-primary-950/95 via-primary-950/85 to-primary-950/60" />
        <div className="absolute inset-0 bg-gradient-to-t from-primary-950 via-transparent to-primary-950/60" />
      </div>

      <div className="relative mx-auto grid max-w-content grid-cols-1 items-center gap-10 px-4 py-16 md:px-6 md:py-20 lg:grid-cols-12 lg:gap-12 lg:py-24">
        <div className="lg:col-span-7">
          <span className="inline-flex items-center gap-2 rounded-full border border-accent-400/50 bg-accent-500/10 px-3 py-1 text-xs font-medium tracking-wide text-accent-200">
            <i className="ri-live-line" aria-hidden="true" />
            每日更新 · 娱乐圈观察频道
          </span>
          <h1 className="mt-5 font-heading text-4xl font-black leading-tight text-background-50 md:text-5xl lg:text-[3.25rem]">
            美日吃瓜
            <span className="mt-2 block text-2xl font-bold text-accent-300 md:text-3xl">
              把娱乐圈热搜，讲成一条看得懂的时间线
            </span>
          </h1>
          <p className="mt-5 max-w-xl text-sm leading-relaxed text-background-200 md:text-base">
            聚焦明星八卦与偶像塌房现场，演员绯闻、歌手翻车、综艺名场面一网打尽。我们不做情绪的放大器，只把来龙去脉整理清楚，让你在评论区开麦之前，先知道发生了什么。
          </p>
          <div className="mt-8 flex flex-col gap-3 sm:flex-row">
            <Link
              to="/category/idol-meltdown"
              className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-accent-500 px-6 py-3 text-sm font-semibold text-primary-950 transition-colors hover:bg-accent-400"
            >
              <i className="ri-fire-line" aria-hidden="true" />
              进入热点频道
            </Link>
            <Link
              to="/faq"
              className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md border border-background-100/40 px-6 py-3 text-sm font-semibold text-background-50 transition-colors hover:border-accent-300 hover:text-accent-200"
            >
              先看看常见问题
              <i className="ri-arrow-right-line" aria-hidden="true" />
            </Link>
          </div>

          <dl className="mt-10 grid max-w-lg grid-cols-2 gap-x-6 gap-y-5 sm:grid-cols-4">
            {stats.map((item) => (
              <div key={item.label}>
                <dt className="font-heading text-2xl font-bold text-accent-300">{item.value}</dt>
                <dd className="mt-1 text-xs text-background-300">{item.label}</dd>
              </div>
            ))}
          </dl>
        </div>

        <div className="lg:col-span-5">
          <Link
            to={`/article/${featured.id}`}
            title={`${featured.title} - 美日吃瓜`}
            className="group block overflow-hidden rounded-lg border border-background-100/15 bg-background-50/10 backdrop-blur transition-colors hover:border-accent-300/60"
          >
            <div className="relative h-52 w-full overflow-hidden md:h-60">
              <img
                src={featured.cover}
                alt={`${featured.title} 配图`}
                title={`${featured.title} - 美日吃瓜`}
                className="h-full w-full object-top transition-transform duration-700 group-hover:scale-105"
              />
              <span className="absolute left-4 top-4 inline-flex items-center gap-1.5 rounded-md bg-primary-500 px-2.5 py-1 text-xs font-medium text-background-50">
                <i className="ri-pushpin-2-fill" aria-hidden="true" />
                头条复盘
              </span>
            </div>
            <div className="p-5 md:p-6">
              <span className="text-xs font-medium tracking-wide text-accent-300">
                {featured.categoryName} · {featured.date}
              </span>
              <h2 className="mt-2 font-heading text-lg font-bold leading-snug text-background-50 transition-colors group-hover:text-accent-200 md:text-xl">
                {featured.title}
              </h2>
              <p className="mt-2 line-clamp-2 text-sm leading-relaxed text-background-300">
                {featured.excerpt}
              </p>
              <span className="mt-4 inline-flex items-center gap-1.5 text-sm font-medium text-accent-300">
                阅读完整时间线
                <i className="ri-arrow-right-line transition-transform group-hover:translate-x-0.5" aria-hidden="true" />
              </span>
            </div>
          </Link>
        </div>
      </div>
    </section>
  );
}